import { useParams } from "react-router-dom";
import { Box, Divider, useColorScheme } from "@mui/joy";
import { Shield, SquareLibrary, User, History } from "lucide-react";

import { IProjectList } from "../../../../../../interfaces";
import { useProject } from "../../../../../../hooks/query";

import { Navigation } from "./Navigation";
import { Basic } from "./Basic";
import { Customer } from "./Customer";
import { Insurance } from "./Insurance";
import { ProjectTimeline } from "./ProjectTimeline";

export default function Overview() {
    const params = useParams();
    const id = params.id as string;

    const { useGetProjectById } = useProject();
    const { data: projectDetails } = useGetProjectById(id);

    return (
        <Box sx={{ width: "100%", mt: -2 }}>
            <Map details={projectDetails} />
            <Divider />
            <Box sx={{ width: "100%" }}>
                <Navigation
                    tabList={[
                        {
                            name: "Basic",
                            icon: <SquareLibrary size={16} strokeWidth={2.5} />,
                            component: <Basic details={projectDetails} />,
                        },
                        {
                            name: "Timeline",
                            icon: <History size={16} strokeWidth={2.5} />,
                            component: (
                                <ProjectTimeline details={projectDetails} />
                            ),
                        },
                        {
                            name: "Customer",
                            icon: <User size={16} strokeWidth={2.5} />,
                            component: <Customer details={projectDetails} />,
                        },
                        {
                            name: "Insurance",
                            icon: <Shield size={16} strokeWidth={2.5} />,
                            component: <Insurance details={projectDetails} />,
                        },
                    ]}
                />
            </Box>
        </Box>
    );
}

function Map({ details }: { details: IProjectList | null }) {
    const { mode } = useColorScheme();
    const isDark = mode === "dark";

    const address = details
        ? `${details.CustomerStreet}, ${details.CustomerCity}, ${details.CustomerZipCode}`
        : "";

    const encodedAddress = encodeURIComponent(address);
    const mapUrl = address
        ? `https://maps.google.com/maps?q=${encodedAddress}&t=&z=13&ie=UTF8&iwloc=&output=embed`
        : "https://maps.google.com/maps?q=Colorado&t=&z=13&ie=UTF8&iwloc=&output=embed";

    const mapFilter = isDark
        ? "invert(1) hue-rotate(180deg) grayscale(0.7) contrast(0.95)"
        : "grayscale(0.7) contrast(1.1)";

    return (
        <Box
            component="iframe"
            title="Project Location Map"
            src={mapUrl}
            sx={{
                display: "block",
                width: "100%",
                height: 180,
                border: 0,
                borderRadius: 0,
                filter: mapFilter,
            }}
        />
    );
}
